import Icon from "../components/NGMaterialIcon/NGMaterialIcon";
import {
  Box,
  BoxProps,
  IconButton,
  IconButtonOwnProps,
  Popper,
  Stack,
  Tooltip,
  TooltipProps,
  Typography,
} from "@mui/material";
import { Signal, useComputed, useSignal, useSignalEffect } from "@preact/signals-react";
import { has, partition, uniq } from "lodash-es";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { getDefaultContext, getObjectData, getScope, getState, meta, setupLocalState } from "../library/dataService";
import { getExprValue } from "../library/interpreter";
import { getTypename, ItemNode } from "../library/metadataUtils";
import { RuntimeContext } from "../library/NGFieldExtensions";
import { isNullOrEmpty } from "../library/utils";

const MAX_ARRAY_SIZE = 100;
const MIN_WIDTH = 360; // Minimum width for the inspector info panel
const INITIAL_WIDTH = 500; // Starting width for the inspector info panel

type InspectorOverlayType = {
  rect: DOMRect;
  node: ItemNode;
  element: Element;
};

type InspectorMode = "controls" | "components";

export default function NGInspector({ config, context }) {
  const local = setupLocalState(
    config,
    {
      Visible: useSignal(false),
    },
    context
  );

  const overlays: Signal<InspectorOverlayType[]> = useSignal([]);
  const currentOverlay: Signal<InspectorOverlayType | null> = useSignal(null);
  const isPinned: Signal<boolean> = useSignal(false);
  const mode: Signal<InspectorMode> = useSignal("components");
  const search = useSignal("");
  const expression = useSignal("");
  const isLeft = useSignal(false);

  const infoRef = useRef<HTMLDivElement>(null);

  const [sashPosition, setSashPosition] = useState(window.innerWidth - INITIAL_WIDTH);
  const [selectParentGlow, setSelectParentGlow] = useState(false);

  const visibleOverlays = useComputed(() => {
    if (mode.value === "components") {
      return overlays.value.filter((o) => ["Page", "Component"].includes(getTypename(o.node.config) ?? ""));
    } else {
      return overlays.value;
    }
  });

  const info = useComputed(() => (currentOverlay.value ? toInfo(currentOverlay.value, mode.value) : null));

  const expressionResult = useComputed(() => {
    const currentExpression = expression.value;
    if (isNullOrEmpty(currentExpression)) return { Result: "Provide an expression" };
    if (currentExpression.trim().endsWith(".")) return { Result: "Incomplete expression" };

    const scope = info.value?.Scope ?? {};
    const error = {};
    const result = getExprValue(currentExpression, scope, null, false, { merge: false, init: false }, error);
    return { Result: has(error, "message") ? "Invalid expression" : result };
  });

  const infoDetails = useComputed(() => {
    const details = info.value?.Details;
    if (!details || !search.value) return details;

    const searchLower = search.value.toLowerCase();

    const filterObject = (obj) => {
      if (typeof obj !== "object" || obj === null) return null;

      const filtered = Object.entries(obj).reduce((acc, [key, value]) => {
        if (key.toLowerCase().includes(searchLower) || String(value).toLowerCase().includes(searchLower)) {
          acc[key] = value;
        } else if (typeof value === "object") {
          const nested = filterObject(value);
          if (nested && Object.keys(nested).length > 0) {
            acc[key] = nested;
          }
        }
        return acc;
      }, {});

      return Object.keys(filtered).length > 0 ? filtered : null;
    };

    return filterObject(details);
  });

  const showDetails = useSignal(true);
  const showEvaluate = useSignal(true);

  const zIndex = 100_000;

  const inspectorInfoWidth = Math.max(isLeft.value ? sashPosition : window.innerWidth - sashPosition, MIN_WIDTH);

  const tooltipSlotProps: TooltipProps["slotProps"] = {
    popper: { sx: { zIndex: zIndex + 1 } },
  };

  const iconButtonStyles: IconButtonOwnProps["sx"] = {
    color: "#fff",
    transition: "color 100ms ease-in-out",
    "&:hover": {
      color: "#f9971f",
    },
    "&.Mui-disabled": {
      color: "#666",
    },
  };

  const inspectorStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex,
    overflow: "visible",
  };

  const downChevron = `url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") no-repeat right 8px center`;

  const inputStyle: CSSProperties = {
    width: "120px",
    borderRadius: "6px",
    color: "black",
    border: "solid 1px rgb(71, 84, 103)",
    height: "36px",
    padding: "0 8px",
  };

  const selectStyle: CSSProperties = {
    ...inputStyle,
    appearance: "none",
    WebkitAppearance: "none", // for Safari
    paddingRight: "32px",
    background: downChevron,
    backgroundColor: "white",
    backgroundSize: "16px",
  };

  const scrollStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    overflow: "auto",
    flex: "1",
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#ddddde",
    },
    "&::-webkit-scrollbar-corner": {
      backgroundColor: "#333",
    },
  };

  const jsonView: any = useSignal(null);

  useEffect(() => {
    (async () => {
      const mod = await import("@/lib/react-json-view/react-json-view-bundle.es.js");
      jsonView.value = mod.default;
    })();
  }, []);

  const ReactJsonView = jsonView.value;
  useEffect(() => {
    const handleKeyDown = (event) => {
      // console.log('inspector key', event.key, event.altKey, event.ctrlKey, event.shiftKey, event);
      if ((event.altKey || event.ctrlKey) && event.shiftKey && event.code.toLowerCase().includes("quote")) {
        local.Visible.value = !local.Visible.peek();
      }

      if (event.key === "Escape") {
        local.Visible.value = false;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useSignalEffect(() => {
    meta.version.value; // observe changes

    if (!local.Visible.value) return;

    buildOverlays();
  });

  useEffect(() => {
    window.addEventListener("scroll", updateRects);
    window.addEventListener("resize", updateRects);

    const handleResize = () => {
      // Keep sash position within bounds when window resizes
      const maxPosition = window.innerWidth - 200;
      if (sashPosition > maxPosition) {
        setSashPosition(maxPosition);
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", updateRects);
      window.removeEventListener("resize", updateRects);
      window.removeEventListener("resize", handleResize);
    };
  }, [sashPosition]);

  useSignalEffect(() => {
    if (isPinned.value) {
      const i = info.peek();
      const d = i.Details;
      console.group(i.Type + ": " + i.Id);
      console.log("Config:", d.Config);
      console.log("State:", d.State);
      console.log("Element:", currentOverlay.peek()?.element);
      console.groupEnd();
    }
  });

  return local.Visible.value ? (
    <>
      <Box className="inspector-overlay" sx={inspectorStyle}>
        {visibleOverlays.value.map((overlay, i) => (
          <InspectorOverlay
            key={i}
            overlay={overlay}
            currentOverlay={currentOverlay}
            isPinned={isPinned}
            glow={selectParentGlow}
          />
        ))}
      </Box>
      {!isPinned.value && (
        <Popper
          placement="top-start"
          sx={{ zIndex }}
          open={!!currentOverlay.value}
          anchorEl={currentOverlay.value?.element}
          modifiers={[{ name: "flip" }, { name: "preventOverflow", options: { padding: 8 } }]}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto auto",
              gap: "8px",
              background: "#333",
              color: "#fff",
              borderRadius: "4px",
              border: "1px solid white",
              padding: "4px 8px",
              fontSize: "12px",
            }}
          >
            <Box sx={{ fontWeight: "bold" }}>Id:</Box>
            <Box>{info.value?.Id}</Box>
            <Box sx={{ fontWeight: "bold" }}>Type:</Box>
            <Box>{info.value?.Type}</Box>
          </Box>
        </Popper>
      )}
      {isPinned.value && (
        <Box
          className="inspector-info"
          ref={infoRef}
          sx={{
            position: "fixed",
            top: "0px",
            ...(isLeft.value ? { left: "0px" } : { left: `${sashPosition}px` }),
            width: `${inspectorInfoWidth}px`,
            zIndex: zIndex + 1,
            height: "100vh",
            background: "#333",
            color: "#fff",
            borderRadius: "4px",
            border: "1px solid white",
            padding: "4px 8px",
            fontSize: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Box
            className="inspector-info-header"
            sx={{
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              height: "40px",
              overflow: "auto",
            }}
          >
            <Typography className="text-heading-small">Settings</Typography>

            <select
              value={mode.value}
              onChange={(e) => (mode.value = e.target.value as InspectorMode)}
              style={selectStyle}
            >
              <option value="components">Components</option>
              <option value="controls">Controls</option>
            </select>

            <Stack direction="row" alignItems="center" justifyContent="flex-end" width="100%">
              {!!info.value?.EditLink && (
                <Tooltip title="Edit" slotProps={tooltipSlotProps}>
                  <IconButton
                    sx={iconButtonStyles}
                    onClick={() => {
                      window.open(info.value?.EditLink, "_blank");
                    }}
                  >
                    <Icon name="edit" />
                  </IconButton>
                </Tooltip>
              )}

              {!!info.value?.PreviewLink && (
                <Tooltip title="Preview" slotProps={tooltipSlotProps}>
                  <IconButton
                    sx={iconButtonStyles}
                    onClick={() => {
                      window.open(info.value?.PreviewLink, "_blank");
                    }}
                  >
                    <Icon name="visibility" />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Select Parent" slotProps={tooltipSlotProps}>
                <IconButton
                  disabled={!currentOverlay.value || !getParentComponent(currentOverlay.value.node)}
                  sx={iconButtonStyles}
                  onClick={() => {
                    if (!currentOverlay.value) return;
                    const parentId = getParentComponent(currentOverlay.value.node);
                    if (!parentId) return;
                    const parentOverlay = overlays.value.find((o) => o.node.Id === parentId);
                    if (!parentOverlay) return;
                    currentOverlay.value = parentOverlay;
                    setSelectParentGlow(true);
                    setTimeout(() => setSelectParentGlow(false), 1000);
                    const type = getTypename(parentOverlay.node.config);
                    mode.value = type === "Component" || type === "Page" ? "components" : "controls";
                  }}
                >
                  {"\u21E7"}
                </IconButton>
              </Tooltip>

              <Tooltip title={isLeft.value ? "Move to right" : "Move to left"} slotProps={tooltipSlotProps}>
                <IconButton
                  sx={iconButtonStyles}
                  onClick={() => {
                    isLeft.value = !isLeft.value;
                    // Adjust sash position when switching sides
                    setSashPosition(window.innerWidth - sashPosition);
                  }}
                >
                  {isLeft.value ? <Icon name="switch_left" /> : <Icon name="switch_right" />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
          <Box sx={{ borderTop: "0.5px solid #fff", marginLeft: "-8px", width: "calc(100% + 16px)" }} />
          <Box sx={{ display: "flex", flexDirection: "row", gap: "1rem", alignItems: "center" }}>
            <Toggle
              expanded={showDetails.value}
              onToggle={() => (showDetails.value = !showDetails.value)}
              title="Details"
            />

            <input
              type="text"
              value={search.value}
              placeholder="Search"
              style={{
                ...inputStyle,
                flex: "1",
              }}
              onInput={(e) => (search.value = e.currentTarget.value)}
            />
          </Box>
          {showDetails.value && (
            <Box className="inspector-info-details" sx={scrollStyle}>
              {info.value && ReactJsonView && (
                <ReactJsonView
                  collapsed={2}
                  displayDataTypes={false}
                  name={info.value.Type}
                  quotesOnKeys={false}
                  shouldCollapse={shouldCollapse}
                  src={infoDetails.value ?? { [search.value]: "Not found" }}
                  style={{ display: "flex", flexDirection: "column", width: "100%", height: "auto", flex: 1 }}
                  theme="monokai"
                />
              )}
            </Box>
          )}
          <Box sx={{ borderTop: "0.5px solid #fff", marginLeft: "-8px", width: "calc(100% + 16px)" }} />
          <Box sx={{ display: "flex", flexDirection: "row", gap: "1rem", alignItems: "center" }}>
            <Toggle
              expanded={showEvaluate.value}
              onToggle={() => (showEvaluate.value = !showEvaluate.value)}
              title="Evaluate"
            />
            <input
              type="text"
              value={expression.value}
              placeholder="Expression. E.g. Row.Name, map(State.Quote, 'Price')"
              style={{
                ...inputStyle,
                flex: "1",
              }}
              onInput={(e) => (expression.value = e.currentTarget.value)}
            />
          </Box>
          {showEvaluate.value && (
            <Box sx={scrollStyle}>
              {ReactJsonView && (
                <ReactJsonView
                  style={{ display: "flex", flexDirection: "column", width: "100%", height: "auto", flex: 1 }}
                  name="Expression"
                  src={expressionResult.value ?? { [expression.value]: "Not valid" }}
                  quotesOnKeys={false}
                  collapsed={2}
                  displayDataTypes={false}
                  theme="monokai"
                />
              )}
            </Box>
          )}
        </Box>
      )}
      {/* Resizable Sash */}
      {isPinned.value && (
        <Sash
          bounds={{ min: MIN_WIDTH, max: window.innerWidth - MIN_WIDTH }}
          onPositionChange={setSashPosition}
          orientation="horizontal"
          position={sashPosition}
          sashSize={4}
        />
      )}
    </>
  ) : (
    <></>
  );

  function updateRects() {
    overlays.value = overlays.value
      .map((overlay) => {
        const rect = getRect(overlay.element);
        if (rect == null) return null; // Skip 0-sized rectangles
        return { ...overlay, rect };
      })
      .filter((x) => x !== null) as InspectorOverlayType[];
  }

  function getRect(element) {
    const rect = element?.getBoundingClientRect() as DOMRect;
    if (rect.width === 0 || rect.height === 0) return null; // Skip 0-sized rectangles

    const adjustedRect = new DOMRect(rect.left + window.scrollX, rect.top + window.scrollY, rect.width, rect.height);
    return adjustedRect;
  }

  function buildOverlays() {
    const elements = document.querySelectorAll("[data-testid]");

    overlays.value = Array.from(elements)
      .map((element, i) => {
        const rect = getRect(element);
        if (rect == null) return null; // Skip 0-sized rectangles
        const testId = element.getAttribute("data-testid");
        const node = meta.getNode(testId);
        if (node == null) return null;

        const overlay = {
          id: node.config.Id,
          type: getTypename(node.config),
          rect,
          node,
          element,
        };
        return overlay;
      })
      .filter((x) => x !== null) as InspectorOverlayType[];
  }

  function shouldCollapse(field) {
    const name = field.name ?? "";

    if (["State"].includes(name)) return false;
    if (["Config", "Context"].includes(name)) return true;

    if (mode.value === "controls") {
      return ["Classes MUI", "State", "Global"].includes(name);
    } else {
      return ["Scope", "Inputs", "Classes", "Global"].includes(name);
    }
  }

  function getParentComponent(node) {
    if (node.context == null || node.context.Path == null) return null;
    for (let i = node.context.Path.length - 2; i >= 0; i--) {
      const parent = node.context.Path[i];
      const type = getTypename(parent.Config);
      if (type === "Component" || type === "Page") {
        return parent.Config.Id;
      }
    }
    return null;
  }

  function toInfo(overlay, mode) {
    const { node, element } = overlay;
    const { Id, config } = node;
    const type = getTypename(config);
    const [classesMui, classes] = partition(
      element.className.split(" "),
      (x) => x.startsWith("Mui") || x.startsWith("css-")
    );

    const context = (node.context as RuntimeContext) ?? getDefaultContext();
    const { global, state, form, parentState } = getState(context);
    const stateData = getObjectData(state, MAX_ARRAY_SIZE);
    const scope = getScope(context, config, state, form, null, parentState);
    const scopeData = getObjectData(scope, MAX_ARRAY_SIZE);
    const globalData = getObjectData(global, MAX_ARRAY_SIZE);
    const scopeSubsetData = getObjectData(
      {
        Row: scope.Row,
        RowIndex: scope.RowIndex,
        Form: scope.Form,
        FormData: scope.FormData,
        Repeater: scope.Repeater,
        Grid: scope.Grid,
      },
      MAX_ARRAY_SIZE
    );

    const baseDetails = {
      Id,
      Type: type,
      Scope: scopeData,
      // Context: cloneDeep(context),
      Details: {
        Id,
        ...(has(config, "Type") ? { Type: config.Type } : {}),
        ...(config.ComponentId ? { ComponentId: config.ComponentId } : {}),
        ParentId: getParentComponent(node),
        Context: node.context,
        Config: config,
        State: stateData,
        Scope: scopeSubsetData,
        Global: globalData,
      },
    };

    if (mode === "components") {
      baseDetails.Details = {
        ...baseDetails.Details,
        ...(type === "Component" && {
          Inputs: Object.keys(config.Inputs ?? {}).reduce((acc, key) => {
            acc[key] = state[key]?.value;
            return acc;
          }, {}),
        }),
      };

      return {
        ...baseDetails,
        PreviewLink: type === "Component" ? `/local/component?Id=${config.Id}` : null,
        EditLink:
          type === "Component"
            ? `/local/component-editor-gui?ModelId=${config.ComponentId ?? config.Id}`
            : window.location.pathname.startsWith("/local")
            ? null
            : `/local/page-editor-gui?ModelId=${config.ComponentId ?? config.Id}`,
      };
    } else if (mode === "elements") {
      return {
        ...baseDetails,
        Details: {
          ...baseDetails.Details,
          Classes: classes,
          "Classes MUI": uniq(classesMui),
        },
      };
    }

    return baseDetails;
  }
}

function InspectorOverlay({ overlay, currentOverlay, isPinned, glow }) {
  const handleMouseEnter = () => {
    if (isPinned.value) return;
    currentOverlay.value = overlay;
  };

  const handleMouseLeave = () => {
    if (isPinned.value) return;
    currentOverlay.value = null;
  };

  const handleClick = () => {
    if (isPinned.value) {
      currentOverlay.value = null;
      isPinned.value = false;
    } else {
      currentOverlay.value = overlay;
      isPinned.value = true;
    }
  };

  const isCurrent = currentOverlay.value?.node.Id === overlay.node.Id;

  return (
    <div
      style={{
        position: "absolute",
        border: `1px dashed cornflowerblue`,
        top: overlay.rect.top,
        left: overlay.rect.left,
        width: overlay.rect.width,
        height: overlay.rect.height,
        zIndex: 999_999,
        ...(isCurrent ? { border: "2px solid cornflowerblue" } : {}),
        boxShadow: isCurrent && glow ? "0 0 10px 5px #528bff" : "none",
        transition: "box-shadow 300ms ease-in-out",
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    ></div>
  );
}

interface ToggleProps extends Omit<BoxProps, "onClick"> {
  expanded: boolean;
  onToggle?: () => void;
}

export function Toggle({ expanded, title, sx, onToggle, ...restProps }: ToggleProps) {
  return (
    <Box sx={{ cursor: "pointer", display: "flex", gap: "4px", ...sx }} {...restProps} onClick={onToggle}>
      <Icon
        name="keyboard_arrow_right"
        style={{
          transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 50ms ease-in-out",
        }}
      />
      {title && (
        <Typography className="text-heading-small" sx={{ minWidth: "72px", userSelect: "none" }}>
          {title}
        </Typography>
      )}
    </Box>
  );
}

export type Orientation = "horizontal" | "vertical";

export interface SashProps {
  bounds?: { min: number; max: number };
  orientation?: Orientation;
  position: number;
  sashSize?: number;
  zIndex?: number;
  onDragEnd?: () => void;
  onDragStart?: () => void;
  onPositionChange: (newPosition: number) => void;
}

export function Sash({
  bounds,
  onDragEnd,
  onDragStart,
  onPositionChange,
  orientation = "horizontal",
  position,
  sashSize = 4,
  zIndex = 100_000,
}: SashProps): JSX.Element {
  // state
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // refs
  const initialOffset = useRef<number | null>(null);
  const originalBodyCursor = useRef<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const sashBarRef = useRef<HTMLDivElement>(null);

  // effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (initialOffset.current === null) return;
      const currentMouse = orientation === "horizontal" ? e.clientX : e.clientY;
      const targetPosition = currentMouse - initialOffset.current;

      // Apply bounds if provided
      let clampedPosition = targetPosition;
      if (bounds) {
        clampedPosition = Math.min(Math.max(targetPosition, bounds.min), bounds.max);
      }

      onPositionChange(clampedPosition);
    };

    const handleMouseUp = () => {
      initialOffset.current = null;
      setIsDragging(false);
      // Restore original cursor
      if (originalBodyCursor.current !== null) {
        document.body.style.cursor = originalBodyCursor.current;
        originalBodyCursor.current = null;
      }
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      onDragEnd?.();
    };

    const handleMouseDown = (e: MouseEvent) => {
      const mousePos = orientation === "horizontal" ? e.clientX : e.clientY;
      // Store the offset between mouse and current sash position
      initialOffset.current = mousePos - position;
      setIsDragging(true);
      onDragStart?.();
      // Store original cursor and set resize cursor on body
      originalBodyCursor.current = document.body.style.cursor || "";
      document.body.style.cursor = orientation === "horizontal" ? "col-resize" : "row-resize";
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    };

    const element = ref.current;
    element?.addEventListener("mousedown", handleMouseDown);
    return () => {
      element?.removeEventListener("mousedown", handleMouseDown);
    };
  }, [onPositionChange, onDragStart, onDragEnd, orientation, position, bounds]);

  // Handle hover state changes
  useEffect(() => {
    const sashBar = sashBarRef.current;
    if (!sashBar) return;

    const shouldShow = isHovered || isDragging;

    if (shouldShow) {
      // Show with delay only if not dragging
      const delay = isDragging ? 0 : 250;
      sashBar.style.transitionDelay = `${delay}ms`;
      sashBar.style.opacity = "1";
    } else {
      // Hide immediately (no delay)
      sashBar.style.transitionDelay = "0ms";
      sashBar.style.opacity = "0";
    }
  }, [isHovered, isDragging]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // render
  return (
    <div
      style={{
        // Self-contained absolute positioning
        position: "absolute",
        top: orientation === "vertical" ? `${position}px` : 0,
        left: orientation === "horizontal" ? `${position}px` : 0,
        width: orientation === "horizontal" ? `${sashSize * 2}px` : "100%",
        height: orientation === "horizontal" ? "100%" : `${sashSize * 2}px`,
        transform: orientation === "horizontal" ? "translateX(-50%)" : "translateY(-50%)",
        pointerEvents: "all",
        zIndex: zIndex + 1,
      }}
    >
      <div
        ref={ref}
        style={{
          // hit area is double the visible sash size
          position: "relative",
          cursor: orientation === "horizontal" ? "col-resize" : "row-resize",
          width: "100%",
          height: "100%",
          userSelect: "none",
          touchAction: "none",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* visible sash bar, centered */}
        <div
          ref={sashBarRef}
          style={{
            position: "absolute",
            top: 0,
            left: orientation === "horizontal" ? "50%" : 0,
            transform: orientation === "horizontal" ? "translateX(-50%)" : undefined,
            backgroundColor: "var(--sash-color, #528bff)",
            width: orientation === "horizontal" ? `${sashSize}px` : "100%",
            height: orientation === "horizontal" ? "100%" : `${sashSize}px`,
            pointerEvents: "none",
            opacity: 0,
            transition: "opacity 200ms ease-in-out",
            transitionDelay: "0ms",
          }}
        />
      </div>
    </div>
  );
}
